import React, {
  type CSSProperties,
  type FC,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog as ShadcnDialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "../dialog";
import { useOverlayPortalContainer } from "../overlay-portal-context";
import { ScrollArea } from "../scroll-area";
import { cn } from "@/lib/utils";
import { useModalExit } from "./modal-presence";
import {
  ScrollableActionContent,
  ScrollableActionActions,
} from "~/src/components/Common/ScrollableActionLayout";
export { ModalPresence } from "./modal-presence";

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  placement?: "center" | "top" | "top-center" | "bottom" | "bottom-center";
  size?:
    | "xs"
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "full";
  radius?: "none" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full";
  scrollBehavior?: "normal" | "inside" | "outside";
  isDismissable?: boolean;
  hideCloseButton?: boolean;
  closeButton?: boolean;
  backdrop?: "transparent" | "opaque" | "blur";
  children: React.ReactNode;
  className?: string;
  classNames?: {
    base?: string;
    backdrop?: string;
    header?: string;
    body?: string;
    footer?: string;
  };
  zIndexBase?: number;
  mobilePresentation?: "fullscreen-sheet" | "dialog";
  variant?: "default" | "confirmation";
}

// Stack counter to offset nested modal z-indexes
let modalStackCounter = 0;

const SIZE_CLASS_MAP: Record<NonNullable<ModalProps["size"]>, string> = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  full: "max-w-full",
};

const RADIUS_CLASS_MAP: Record<NonNullable<ModalProps["radius"]>, string> = {
  none: "rounded-none sm:rounded-none",
  sm: "rounded-sm sm:rounded-sm",
  md: "rounded-md sm:rounded-md",
  lg: "rounded-lg sm:rounded-lg",
  xl: "rounded-xl sm:rounded-xl",
  "2xl": "rounded-2xl sm:rounded-2xl",
  "3xl": "rounded-3xl sm:rounded-3xl",
  full: "rounded-full sm:rounded-full",
};

const PLACEMENT_CLASS_MAP: Record<
  NonNullable<ModalProps["placement"]>,
  string
> = {
  center: "left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]",
  top: "left-[50%] top-4 translate-x-[-50%] translate-y-0",
  "top-center": "left-[50%] top-4 translate-x-[-50%] translate-y-0",
  bottom: "left-[50%] bottom-4 translate-x-[-50%] translate-y-0",
  "bottom-center": "left-[50%] bottom-4 translate-x-[-50%] translate-y-0",
};

const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  placement = "center",
  size = "md",
  radius = "3xl",
  scrollBehavior = "normal",
  isDismissable = true,
  hideCloseButton,
  closeButton,
  children,
  className,
  classNames,
  zIndexBase,
  mobilePresentation = "dialog",
  variant = "default",
}) => {
  const parentDialog = useOverlayPortalContainer();
  const parentZIndex = Number(parentDialog?.style.zIndex) || 0;
  const shouldHideCloseButton = hideCloseButton ?? closeButton === false;
  const { exiting, complete } = useModalExit();

  const [computedZIndexBase, setComputedZIndexBase] = useState<number>(60);
  const hasIncrementedRef = useRef<boolean>(false);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      const next = modalStackCounter + 1;
      modalStackCounter = next;
      hasIncrementedRef.current = true;
      // A bottom sheet is a Dialog too, but is not counted in modalStackCounter.
      const baseFromProp = zIndexBase ?? Math.max(60, parentZIndex + 1);
      setComputedZIndexBase(baseFromProp + (next - 1) * 20);
    } else {
      hasIncrementedRef.current = false;
    }
    return () => {
      if (hasIncrementedRef.current) {
        modalStackCounter = Math.max(0, modalStackCounter - 1);
        hasIncrementedRef.current = false;
      }
    };
  }, [isOpen, zIndexBase, parentZIndex]);

  const sizeClass = useMemo(() => SIZE_CLASS_MAP[size] ?? "max-w-md", [size]);

  const radiusClass = useMemo(
    () => RADIUS_CLASS_MAP[radius] ?? "rounded-3xl sm:rounded-3xl",
    [radius]
  );

  const placementClass = useMemo(
    () => PLACEMENT_CLASS_MAP[placement] ?? PLACEMENT_CLASS_MAP.center,
    [placement]
  );

  const hasModalHeader = useMemo(() => {
    return React.Children.toArray(children).some((child) => {
      if (!React.isValidElement(child)) {
        return false;
      }

      const type = child.type as any;
      return (
        type?.displayName === "ModalHeader" || type?.name === "ModalHeader"
      );
    });
  }, [children]);

  return (
    <ShadcnDialog
      open={isOpen && !exiting}
      onOpenChange={(open) => {
        if (!open && isOpen && !exiting) onClose();
      }}
      modal={true}
    >
      <DialogContent
        forceMount={exiting ? true : undefined}
        inert={exiting ? true : undefined}
        onAnimationEnd={(event) => {
          if (
            exiting &&
            event.target === event.currentTarget &&
            (event.animationName === "mobile-sheet-close" ||
              event.animationName === "modal-content-close")
          )
            complete();
        }}
        mobilePresentation={mobilePresentation}
        className={cn(
          placementClass,
          sizeClass,
          radiusClass,
          scrollBehavior === "inside" && "max-h-[90vh] overflow-y-auto",
          shouldHideCloseButton && "[&>button]:hidden",
          variant === "confirmation"
            ? "w-[calc(100%-32px)] md:w-full"
            : "w-[calc(100%-16px)] md:w-full",
          variant === "confirmation" &&
            !shouldHideCloseButton &&
            "[&_[data-modal-part=header]]:px-3",
          classNames?.base,
          className
        )}
        overlayClassName={classNames?.backdrop}
        zIndexBase={computedZIndexBase}
        aria-describedby={undefined}
        onOpenAutoFocus={() => {
          const activeElement = document.activeElement;
          if (
            activeElement instanceof HTMLElement &&
            (mobilePresentation === "fullscreen-sheet" ||
              activeElement.closest(
                '[data-mobile-presentation="fullscreen-sheet"]'
              ))
          ) {
            returnFocusRef.current = activeElement;
          }
        }}
        onCloseAutoFocus={(event) => {
          if (returnFocusRef.current?.isConnected) {
            event.preventDefault();
            returnFocusRef.current.focus({ preventScroll: true });
          }
          returnFocusRef.current = null;
        }}
        onInteractOutside={(e) => {
          if (!isDismissable) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!isDismissable) {
            e.preventDefault();
          }
        }}
      >
        {!hasModalHeader && mobilePresentation !== "fullscreen-sheet" && (
          <DialogTitle className="sr-only">Dialog</DialogTitle>
        )}
        {children}
      </DialogContent>
    </ShadcnDialog>
  );
};

export interface ModalContentProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

const ModalContent: React.FC<ModalContentProps> = ({
  children,
  className,
  style,
}) => {
  return (
    <div data-modal-part="content" className={className} style={style}>
      {children}
    </div>
  );
};

export interface ModalHeaderProps {
  children: React.ReactNode;
  className?: string;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ children, className }) => {
  // Check if children already contains DialogTitle by examining React children
  const childrenArray = React.Children.toArray(children);
  const hasDialogTitle = childrenArray.some((child) => {
    if (React.isValidElement(child)) {
      // Check if it's DialogTitle component
      if (child.type === DialogTitle) return true;
      // Check displayName for forwardRef components
      const type = child.type as any;
      if (type?.displayName === DialogTitle.displayName) return true;
      // Check if it's a Radix primitive Title
      if (type?.$$typeof?.toString().includes("react.forward_ref")) {
        return type?.render?.displayName === DialogTitle.displayName;
      }
    }
    return false;
  });

  return (
    <DialogHeader data-modal-part="header" className={cn("pb-4", className)}>
      {hasDialogTitle ? (
        children
      ) : typeof children === "string" ? (
        <DialogTitle>{children}</DialogTitle>
      ) : React.Children.count(children) === 1 &&
        React.isValidElement(children) ? (
        <DialogTitle asChild>{children}</DialogTitle>
      ) : (
        // If multiple children, wrap in a fragment and don't use asChild
        <DialogTitle>{children}</DialogTitle>
      )}
    </DialogHeader>
  );
};
ModalHeader.displayName = "ModalHeader";

export interface ModalBodyProps {
  children: React.ReactNode;
  className?: string;
  /** Inner content classes. Omit to retain the legacy duplicated className. */
  contentClassName?: string;
  /** Actions rendered outside the scrolling body, including in fullscreen sheets. */
  footer?: React.ReactNode;
  /**
   * If true, wraps children in ScrollArea component for better scroll behavior
   * @default true
   */
  useScrollArea?: boolean;
}

const ModalBody: React.FC<ModalBodyProps> = ({
  children,
  className,
  contentClassName,
  useScrollArea = true,
  footer,
}) => {
  return (
    <>
      {useScrollArea ? (
        <ScrollableActionContent
          scroll="external"
          data-modal-part="body"
          className={cn("pt-6 px-0 flex flex-col min-h-0", className)}
        >
          <ScrollArea className="w-full flex-1 min-h-0 px-4">
            <div
              data-modal-part="body-content"
              className={contentClassName ?? className ?? ""}
            >
              {children}
            </div>
          </ScrollArea>
        </ScrollableActionContent>
      ) : (
        <ScrollableActionContent
          scroll="external"
          data-modal-part="body"
          data-modal-scroll="native"
          className={cn("pt-4 px-4", className)}
        >
          {children}
        </ScrollableActionContent>
      )}
      {footer != null && footer !== false && (
        <ModalFooter>{footer}</ModalFooter>
      )}
    </>
  );
};

export interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

const ModalFooter: React.FC<ModalFooterProps> = ({ children, className }) => {
  return (
    <ScrollableActionActions asChild>
      <DialogFooter
        data-modal-part="footer"
        className={cn(
          "w-full flex flex-row items-center gap-2 mt-4",
          className
        )}
      >
        {children}
      </DialogFooter>
    </ScrollableActionActions>
  );
};

export { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter };
